/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { EventEmitter, Injectable, NgZone } from "@angular/core";

import { MessageBus, MessageBusSink, MessageBusSource } from "./message_bus";

interface SharedWorker extends EventTarget, AbstractWorker {
  /**
   * Returns sharedWorker's MessagePort object which can be used to communicate with the global environment.
   */
  readonly port: MessagePort;
  addEventListener<K extends keyof AbstractWorkerEventMap>(
    type: K,
    listener: (this: SharedWorker, ev: AbstractWorkerEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions,
  ): void;
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener<K extends keyof AbstractWorkerEventMap>(
    type: K,
    listener: (this: SharedWorker, ev: AbstractWorkerEventMap[K]) => any,
    options?: boolean | EventListenerOptions,
  ): void;
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void;
  onconnect(event: { ports: MessagePort[] }): void;
}

declare var SharedWorker: {
  prototype: SharedWorker;
  new (scriptURL: string, options?: string | WorkerOptions): SharedWorker;
};

// TODO(jteplitz602) Replace this with the definition in lib.webworker.d.ts(#3492)
export interface PostMessageTarget {
  postMessage: (message: any, transfer?: Array<unknown>) => void;
}

class TransferableData {
  public data: Object;
  public transfer: Array<Transferable> = [];
}

export class PostMessageBusSink implements MessageBusSink {
  private _zone: NgZone;
  private _channels: { [key: string]: _Channel } = {};
  private _transferChannel: _Channel = null;
  private _messageBuffer: Array<Object> = [];
  private _transferBuffer: Array<Transferable> = [];

  constructor(private _postMessageTarget: any) {}

  attachToZone(zone: NgZone): void {
    this._zone = zone;
    this._zone.runOutsideAngular(() => {
      this._zone.onStable.subscribe({
        next: () => {
          this._handleOnEventDone();
        },
      });
    });
  }

  initChannel(channel: string, runInZone: boolean = true, hasTransfers: boolean = false): void {
    if (this._channels.hasOwnProperty(channel)) {
      throw new Error(`${channel} has already been initialized`);
    }

    const emitter = new EventEmitter(false);
    const channelInfo = new _Channel(emitter, runInZone);
    this._channels[channel] = channelInfo;

    emitter.subscribe((ev: TransferableData) => {
      let data = ev.data;
      let transfer = ev.transfer;

      const message = { channel: channel, message: data };

      if (runInZone) {
        this._messageBuffer.push(message);

        if (Array.isArray(transfer)) {
          this._transferBuffer.push(...transfer);
        }
      } else if (Array.isArray(transfer)) {
        this._sendMessagesWithTransfer([message], transfer);
      } else {
        this._sendMessages([message]);
      }

      // onStable may have already fired
      if (this._zone && this._zone.isStable) {
        this._handleOnEventDone();
      }
    });
  }

  to(channel: string): EventEmitter<any> {
    if (this._channels.hasOwnProperty(channel)) {
      return this._channels[channel].emitter;
    } else {
      throw new Error(`${channel} is not set up. Did you forget to call initChannel?`);
    }
  }

  private _handleOnEventDone() {
    // tranferBuffer may be shared between multiple messages without consequence

    if (this._messageBuffer.length > 0 && this._transferBuffer.length === 0) {
      this._sendMessages(this._messageBuffer);
      this._messageBuffer = [];
    } else if (this._messageBuffer.length > 0) {
      this._sendMessagesWithTransfer(this._messageBuffer, this._transferBuffer);
      this._messageBuffer = [];
      this._transferBuffer = [];
    }
  }

  protected _sendMessages(messages: Array<Object>) {
    //logger.out.info(`PostMessageBusSink _sendMessages: ${self.location.hash} %O`, messages);
    this._postMessageTarget.postMessage(messages);
  }

  protected _sendMessagesWithTransfer(messages: Array<Object>, transfer?: Transferable[]) {
    //logger.out.info(`PostMessageBusSink _sendMessagesWithTransfer: ${self.location.hash} %O %O`, messages, transfer);
    this._postMessageTarget.postMessage(messages, transfer);
  }
}

export class PrimaryMessageBusSink extends PostMessageBusSink {
  private _postMessageTargets: Array<SharedWorker> = [];

  constructor() {
    super(null);
  }

  addWorker(worker: any) {
    this._postMessageTargets.push(worker.port);
  }

  protected _sendMessages(messages: Array<Object>) {
    // Used only for registry responses

    for (let postMessageTarget of this._postMessageTargets) {
      //logger.out.info(`PrimaryMessageBusSink _sendMessages: ${self.location.hash} %O`, messages);
      postMessageTarget.port.postMessage(messages);
    }
  }

  protected _sendMessagesWithTransfer(messages: Array<Object>, transfer?: Transferable[]) {
    // Used only for registry responses

    for (let postMessageTarget of this._postMessageTargets) {
      //logger.out.info(`PrimaryMessageBusSink _sendMessagesWithTransfer: ${self.location.hash} %O %O`, messages, transfer);
      postMessageTarget.port.postMessage(messages, transfer);
    }
  }
}

export class PostMessageBusSource implements MessageBusSource {
  private _zone: NgZone;
  private _channels: { [key: string]: _Channel } = {};

  constructor(eventTarget?: MessagePort) {
    if (eventTarget) {
      eventTarget.addEventListener("message", (ev: MessageEvent) => this._handleMessages(ev));
    } else {
      // if no eventTarget is given we assume we're in a WebWorker and listen on the global scope
      const workerScope = self as unknown as SharedWorker;
      workerScope.port.addEventListener("message", (ev: MessageEvent) => this._handleMessages(ev));
    }
  }

  attachToZone(zone: NgZone) {
    this._zone = zone;
  }

  initChannel(channel: string, runInZone: boolean = true, hasTransfers: boolean = false) {
    if (this._channels.hasOwnProperty(channel)) {
      throw new Error(`${channel} has already been initialized`);
    }

    const emitter = new EventEmitter(false);
    const channelInfo = new _Channel(emitter, runInZone);
    this._channels[channel] = channelInfo;
  }

  from(channel: string): EventEmitter<any> {
    if (this._channels.hasOwnProperty(channel)) {
      return this._channels[channel].emitter;
    } else {
      throw new Error(`${channel} is not set up. Did you forget to call initChannel?`);
    }
  }

  protected _handleMessages(ev: MessageEvent): void {
    const messages = ev.data;
    for (let i = 0; i < messages.length; i++) {
      this._handleMessage(messages[i]);
    }
  }

  protected _handleMessage(data: any): void {
    const channel = data.channel;
    if (this._channels.hasOwnProperty(channel)) {
      const channelInfo = this._channels[channel];
      if (channelInfo.runInZone) {
        this._zone.run(() => {
          channelInfo.emitter.emit(data.message);
        });
      } else {
        channelInfo.emitter.emit(data.message);
      }
    }
  }
}

export class PrimaryMessageBusSource extends PostMessageBusSource {
  constructor(eventTarget?: EventTarget) {
    let noopEventTarget: any = new Object();
    noopEventTarget["addEventListener"] = function (type: string, handler: any, useCapture) {
      //no op
    };

    super(noopEventTarget);
  }

  addWorker(eventTarget?: SharedWorker) {
    if (eventTarget) {
      eventTarget.port.addEventListener("message", (ev: MessageEvent) => this._handleMessages(ev));
    } else {
      // if no eventTarget is given we assume we're in a WebWorker and listen on the global scope
      const workerScope = self as unknown as SharedWorker;
      workerScope.onconnect = function (e) {
        let port = e.ports[0];
        port.addEventListener("message", (ev: MessageEvent) => this._handleMessages(ev));
        port.start();
      };
    }
  }
}

/**
 * A TypeScript implementation of {@link MessageBus} for communicating via JavaScript's
 * postMessage API.
 */
@Injectable()
export class PostMessageBus implements MessageBus {
  constructor(public sink: PostMessageBusSink, public source: PostMessageBusSource) {}

  attachToZone(zone: NgZone): void {
    this.source.attachToZone(zone);
    this.sink.attachToZone(zone);
  }

  initChannel(channel: string, runInZone: boolean = true, hasTransfers: boolean = false): void {
    this.source.initChannel(channel, runInZone, hasTransfers);
    this.sink.initChannel(channel, runInZone, hasTransfers);
  }

  from(channel: string): EventEmitter<any> {
    return this.source.from(channel);
  }

  to(channel: string): EventEmitter<any> {
    return this.sink.to(channel);
  }
}

@Injectable()
export class PrimaryMessageBus extends PostMessageBus {
  constructor() {
    super(new PrimaryMessageBusSink(), new PrimaryMessageBusSource());
  }

  addWorker(worker: SharedWorker) {
    (this.sink as PrimaryMessageBusSink).addWorker(worker);
    (this.source as PrimaryMessageBusSource).addWorker(worker);
  }
}

/**
 * Helper class that wraps a channel's {@link EventEmitter} and
 * keeps track of if it should run in the zone.
 */
class _Channel {
  constructor(public emitter: EventEmitter<any>, public runInZone: boolean) {}
}
